import { zip } from 'es-toolkit';

import { notNullish } from '@paragon/utilities';

export const formatLengthToArchitecturalString = (lengthInInches: number) => {
  const {
    negativeString,
    feetDecoratorString,
    feetString,
    fractionalInchesDecoratorString,
    fractionalInchString,
    inchDecoratorString,
    inchString,
  } = getImperialLengthTextObject(lengthInInches);
  return (
    negativeString +
    feetString +
    feetDecoratorString +
    inchString +
    inchDecoratorString +
    fractionalInchString +
    fractionalInchesDecoratorString
  );
};

export const formatLengthToFeetInchesSixteenthsString = (lengthInInches: number) => {
  const { feet, inches, sixteenthInches } = getImperialLengthUnitObject(Math.abs(lengthInInches));
  const negativeString = lengthInInches < 0 ? '-' : '';
  return `${negativeString}${feet}-${inches}-${sixteenthInches}`;
};

interface ImperialLengthObject {
  feet: number;
  inches: number;
  sixteenthInches: number;
}

const SIXTEENTHS_IN_INCH = 16;
const INCHES_IN_FOOT = 12;
const SIXTEENTHS_IN_FOOT = SIXTEENTHS_IN_INCH * INCHES_IN_FOOT;

export const getImperialLengthUnitObject = (lengthInInches: number): ImperialLengthObject => {
  const lengthInSixteenths = Math.round(lengthInInches * SIXTEENTHS_IN_INCH);
  return {
    feet: Math.trunc(lengthInSixteenths / SIXTEENTHS_IN_FOOT),
    inches: Math.trunc((lengthInSixteenths % SIXTEENTHS_IN_FOOT) / SIXTEENTHS_IN_INCH),
    sixteenthInches: lengthInSixteenths % SIXTEENTHS_IN_INCH,
  };
};

/**
 * Compute whether the input distance can be represented with precision at least as great as the industry tolerance.
 * @param lengthInInches The distance to test for safety.
 * @see Number.isSafeInteger()
 * @link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/isSafeInteger
 */
const isSafeDistance = (lengthInInches: number) =>
  Number.isSafeInteger(Math.round(lengthInInches * SIXTEENTHS_IN_INCH));

export interface ImperialLengthStringObject {
  negativeString: string;
  feetString: string;
  feetDecoratorString: string;
  inchString: string;
  inchDecoratorString: string;
  fractionalInchString: string;
  fractionalInchesDecoratorString: string;
}

export const getImperialLengthTextObject = (lengthInInches: number): ImperialLengthStringObject => {
  const { feet, inches, sixteenthInches } = getImperialLengthUnitObject(Math.abs(lengthInInches));
  const negativeString = lengthInInches < 0 ? '-' : '';
  let feetString = '';
  let feetDecoratorString = '';
  let inchString = '';
  let inchDecoratorString = '';
  let fractionalInchString = '';
  let fractionalInchesDecoratorString = '';
  if (feet !== 0 || (!inches && !sixteenthInches)) {
    feetString = feet.toString();
    feetDecoratorString = "'";
    if (inches || sixteenthInches) {
      feetDecoratorString += ' ';
    }
  }

  if (inches !== 0) {
    inchString = inches.toString();
  }
  if (sixteenthInches !== 0) {
    if (inches !== 0) {
      inchDecoratorString = '-';
    }
    let baseUnitOfSixteen = 16;
    let fractionalSixteenthInches = sixteenthInches;
    while (baseUnitOfSixteen % 2 === 0 && fractionalSixteenthInches % 2 === 0) {
      baseUnitOfSixteen /= 2;
      fractionalSixteenthInches /= 2;
    }
    fractionalInchString = `${fractionalSixteenthInches.toString()}/${baseUnitOfSixteen.toString()}`;
    fractionalInchesDecoratorString = '"';
  } else if (inches !== 0) {
    inchDecoratorString = '"';
  }
  return {
    negativeString,
    feetString,
    feetDecoratorString,
    inchString,
    inchDecoratorString,
    fractionalInchString,
    fractionalInchesDecoratorString,
  };
};

export const parseImperialLengthToInches = (architecturalString: string) =>
  parse(architecturalString, [digitsParser, footPointParser, architecturalParser, fisParser]);

export const parseImperialLengthSummationExpression = (input: string) => {
  const operators = /\+| - /g;
  const parsedDistanceLiterals = input.split(operators).map(parseImperialLengthToInches);
  if (!parsedDistanceLiterals.every(notNullish)) return null;

  const operationsAsCoefficients = input.match(operators)?.map((operand) => (operand === ' - ' ? -1 : 1)) ?? [];
  operationsAsCoefficients.unshift(1);
  if (operationsAsCoefficients.length !== parsedDistanceLiterals.length) return null;

  return zip(parsedDistanceLiterals, operationsAsCoefficients)
    .map(([distance, coefficient]) => distance! * coefficient!)
    .reduce((a, b) => a + b);
};

const calcDistance = (length: ImperialLengthObject, negative: boolean): number | null => {
  const { feet, inches, sixteenthInches } = length;
  const distance = (feet * INCHES_IN_FOOT + inches + sixteenthInches / SIXTEENTHS_IN_INCH) * (negative ? -1 : 1);
  return isSafeDistance(distance) ? distance : null;
};

type DistanceParser = (input: string) => ImperialLengthObject | null;

const preprocess = (input: string): { trimmed: string; negative: boolean } | null => {
  const match = /^(-)?(.*)$/.exec(input.trim());
  if (!match) return null;

  const negative = !!match[1];
  const trimmed = match[2];
  if (trimmed === '') return null; // too bad there are no possessive qualifiers in JavaScript
  return { trimmed, negative };
};

const parse = (input: string, parsers: DistanceParser[]): number | null => {
  const preprocessed = preprocess(input);
  if (!preprocessed) return null;
  const { trimmed, negative } = preprocessed;

  for (const parser of parsers) {
    const parsed = parser(trimmed);
    if (parsed !== null) return calcDistance(parsed, negative);
  }
  return null;
};

const fisParser: DistanceParser = (input) => {
  // f+-ii?-ss?
  const match = /^(\d+)-(\d{1,2})-(\d{1,2})$/.exec(input);
  return match ? { feet: Number(match[1]), inches: Number(match[2]), sixteenthInches: Number(match[3]) } : null;
};

const digitsParser: DistanceParser = (input) => {
  // 3+ digits, f+iiss or i?iss
  const match = /^(?:(?:(\d+)(\d{2}))|(\d+))(\d{2})$/.exec(input);
  if (!match) return null;
  const inches = match[2] || match[3];
  return { feet: Number(match[1] || 0), inches: Number(inches), sixteenthInches: Number(match[4]) };
};

const footPointParser: DistanceParser = (input) => {
  // whole feet succeeded by period, f+\.
  const match = /^\d+\.$/.exec(input);
  return match ? { feet: Number(match[0]), inches: 0, sixteenthInches: 0 } : null;
};

const architecturalParser: DistanceParser = (input) => {
  // ^(?:                // optional feet
  //     ([\d.]+)        // 1: decimal feet
  //     ['‘’]           // foot sign (straight apostrophe or left/right single quotation mark produced on iOS)
  // )?
  // [\s-]*              // any whitespace or hyphens
  // (?:
  //     (?:
  //         (\d+)??     // 2: inches (reluctant, match numerator first)
  //         [\s-]*      // any whitespace or hyphens
  //         (?:         // optional fraction: n+/d+
  //             (\d+)   // 3: numerator
  //             \/      // fraction sign
  //             (\d+)   // 4: denominator
  //         )?
  //         |           // or
  //         ([\d.]+)    // 5: decimal inches
  //     )
  //     ["“”]?          // optional inch sign (straight quote or left/right curly quote produced on iOS)
  // )$
  const match = /^(?:([\d.]+)['‘’])?[\s-]*(?:(?:(\d+)??[\s-]*(?:(\d+)\/(\d+))?|([\d.]+))["“”]?)$/u.exec(input);
  if (!match || /^["“”]$/u.test(input)) return null;
  const inches = Number(match[2] || match[5] || 0);
  const fraction = match[3] ? Number(match[3]) / Number(match[4]) : 0;
  return match ? { feet: Number(match[1] || 0), inches: inches + fraction, sixteenthInches: 0 } : null;
};
